import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

export type MobileAlertTone = 'success' | 'error' | 'warning' | 'info' | 'question';

export interface MobileAlertConfig {
  title: string;
  text?: string;
  html?: string;
  tone?: MobileAlertTone;
  showCancel?: boolean;
  confirmText?: string;
  cancelText?: string;
  confirmTone?: 'danger' | 'primary' | 'success' | 'neutral';
  allowOutsideClick?: boolean;
  /** Mode chargement sans boutons */
  loading?: boolean;
  /** Champ simple (remplace Swal input text/textarea) */
  input?: 'text' | 'textarea';
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue?: string;
  /** Callback Swal didOpen (DOM prêt) */
  didOpen?: () => void;
  /** Callback Swal preConfirm — retour false pour bloquer */
  preConfirm?: () => unknown;
}

export interface MobileAlertState extends MobileAlertConfig {
  open: boolean;
}

const EMPTY_SWAL_RESULT: SweetAlertResult = {
  isConfirmed: false,
  isDenied: false,
  isDismissed: true,
  value: undefined,
  dismiss: Swal.DismissReason.cancel
};

@Injectable({ providedIn: 'root' })
export class MobileAlertService {
  private readonly stateSubject = new BehaviorSubject<MobileAlertState | null>(null);
  readonly state$ = this.stateSubject.asObservable();

  private resolveFn: ((result: SweetAlertResult) => void) | null = null;
  private swalPatched = false;
  private originalSwalFire: typeof Swal.fire | null = null;

  constructor() {
    this.patchSwalBridge();
  }

  /** Petits écrans / téléphones */
  isMobileViewport(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(max-width: 768px)').matches;
  }

  get snapshot(): MobileAlertState | null {
    return this.stateSubject.value;
  }

  async confirm(config: {
    title: string;
    text?: string;
    confirmText?: string;
    cancelText?: string;
    confirmTone?: MobileAlertConfig['confirmTone'];
    tone?: MobileAlertTone;
  }): Promise<boolean> {
    const result = await this.open({
      title: config.title,
      text: config.text,
      tone: config.tone ?? 'question',
      showCancel: true,
      confirmText: config.confirmText ?? 'Confirmer',
      cancelText: config.cancelText ?? 'Annuler',
      confirmTone: config.confirmTone ?? 'primary',
      allowOutsideClick: true
    });
    return result.isConfirmed;
  }

  async alert(config: {
    title: string;
    text?: string;
    tone?: MobileAlertTone;
    confirmText?: string;
  }): Promise<void> {
    await this.open({
      title: config.title,
      text: config.text,
      tone: config.tone ?? 'info',
      showCancel: false,
      confirmText: config.confirmText ?? 'OK',
      confirmTone: config.tone === 'error' ? 'danger' : config.tone === 'success' ? 'success' : 'primary',
      allowOutsideClick: true
    });
  }

  showLoading(title: string = 'Chargement...'): void {
    this.dismissCurrent({ isConfirmed: false, isDenied: false, isDismissed: true, value: undefined });
    this.stateSubject.next({
      open: true,
      title,
      loading: true,
      showCancel: false,
      allowOutsideClick: false
    });
  }

  close(): void {
    this.dismissCurrent({
      isConfirmed: false,
      isDenied: false,
      isDismissed: true,
      value: undefined,
      dismiss: Swal.DismissReason.cancel
    });
    this.stateSubject.next(null);
  }

  open(config: MobileAlertConfig): Promise<SweetAlertResult> {
    this.dismissCurrent({
      isConfirmed: false,
      isDenied: false,
      isDismissed: true,
      value: undefined,
      dismiss: Swal.DismissReason.cancel
    });

    return new Promise<SweetAlertResult>((resolve) => {
      this.resolveFn = resolve;
      this.stateSubject.next({
        open: true,
        title: config.title || '',
        text: config.text,
        html: config.html,
        tone: config.tone ?? 'info',
        showCancel: !!config.showCancel,
        confirmText: config.confirmText ?? 'OK',
        cancelText: config.cancelText ?? 'Annuler',
        confirmTone: config.confirmTone ?? 'primary',
        allowOutsideClick: config.allowOutsideClick !== false,
        loading: !!config.loading,
        input: config.input,
        inputLabel: config.inputLabel,
        inputPlaceholder: config.inputPlaceholder,
        inputValue: config.inputValue ?? '',
        didOpen: config.didOpen,
        preConfirm: config.preConfirm
      });
    });
  }

  /** Appelé par le composant après rendu */
  notifyOpened(): void {
    const state = this.stateSubject.value;
    if (state?.didOpen) {
      try {
        state.didOpen();
      } catch {
        // ignore
      }
    }
  }

  async confirmClick(inputValue?: string): Promise<void> {
    const state = this.stateSubject.value;
    if (!state || state.loading) {
      return;
    }

    let value: unknown = state.input ? (inputValue ?? '') : true;
    if (state.preConfirm) {
      try {
        const pre = await Promise.resolve(state.preConfirm());
        if (pre === false) {
          return;
        }
        value = pre === undefined ? value : pre;
      } catch {
        return;
      }
    }

    this.finish({
      isConfirmed: true,
      isDenied: false,
      isDismissed: false,
      value
    });
  }

  cancelClick(): void {
    this.finish({
      isConfirmed: false,
      isDenied: false,
      isDismissed: true,
      value: undefined,
      dismiss: Swal.DismissReason.cancel
    });
  }

  backdropClick(): void {
    const state = this.stateSubject.value;
    if (state?.allowOutsideClick && !state.loading) {
      this.cancelClick();
    }
  }

  private finish(result: SweetAlertResult): void {
    const resolver = this.resolveFn;
    this.resolveFn = null;
    this.stateSubject.next(null);
    resolver?.(result);
  }

  private dismissCurrent(result: SweetAlertResult): void {
    if (this.resolveFn) {
      const resolver = this.resolveFn;
      this.resolveFn = null;
      resolver(result);
    }
  }

  /**
   * Sur mobile, tous les Swal.fire passent par notre modale (boutons tactiles fiables).
   */
  private patchSwalBridge(): void {
    if (this.swalPatched) {
      return;
    }
    this.swalPatched = true;
    this.originalSwalFire = Swal.fire.bind(Swal) as typeof Swal.fire;

    const self = this;
    (Swal as any).fire = async (...args: any[]) => {
      if (!self.isMobileViewport()) {
        return (self.originalSwalFire as any)(...args);
      }

      const options = self.normalizeSwalArgs(args);

      // Loading Swal (pas de bouton confirm)
      if (options.showConfirmButton === false || (options.didOpen && options.allowOutsideClick === false && !options.showCancelButton)) {
        const isLoadingOnly =
          options.showConfirmButton === false ||
          (typeof options.didOpen === 'function' && options.showCancelButton !== true && !options.confirmButtonText);
        if (options.showConfirmButton === false) {
          self.showLoading(String(options.title || 'Chargement...'));
          return new Promise(() => {
            /* résolu via close() */
          });
        }
        if (isLoadingOnly && !options.html && !options.text && !options.input) {
          self.showLoading(String(options.title || 'Chargement...'));
          return new Promise(() => undefined);
        }
      }

      return self.open({
        title: self.stringifyTitle(options.title),
        text: options.text ? String(options.text) : undefined,
        html: options.html ? String(options.html) : undefined,
        tone: self.mapIcon(options.icon),
        showCancel: !!options.showCancelButton,
        confirmText: options.confirmButtonText ? String(options.confirmButtonText) : 'OK',
        cancelText: options.cancelButtonText ? String(options.cancelButtonText) : 'Annuler',
        confirmTone: self.mapConfirmTone(options),
        allowOutsideClick: options.allowOutsideClick !== false,
        input:
          options.input === 'textarea' || options.input === 'text'
            ? options.input
            : undefined,
        inputLabel: options.inputLabel ? String(options.inputLabel) : undefined,
        inputPlaceholder: options.inputPlaceholder
          ? String(options.inputPlaceholder)
          : undefined,
        inputValue: options.inputValue != null ? String(options.inputValue) : '',
        didOpen: typeof options.didOpen === 'function' ? () => options.didOpen!(undefined as any) : undefined,
        preConfirm: typeof options.preConfirm === 'function' ? () => options.preConfirm!(undefined as any) : undefined
      });
    };

    const originalClose = Swal.close.bind(Swal);
    (Swal as any).close = (...args: any[]) => {
      if (self.isMobileViewport() && self.snapshot?.open) {
        self.close();
        return;
      }
      return originalClose(...args);
    };
  }

  private normalizeSwalArgs(args: any[]): SweetAlertOptions {
    if (args.length === 0) {
      return {};
    }
    if (typeof args[0] === 'object' && args[0] !== null && !Array.isArray(args[0])) {
      return args[0] as SweetAlertOptions;
    }
    return {
      title: args[0],
      text: args[1],
      icon: args[2]
    } as SweetAlertOptions;
  }

  private stringifyTitle(title: SweetAlertOptions['title']): string {
    if (title == null) {
      return '';
    }
    if (typeof title === 'string') {
      // Titre parfois en HTML (disponibilités)
      if (title.includes('<')) {
        const tmp = document.createElement('div');
        tmp.innerHTML = title;
        return (tmp.textContent || tmp.innerText || '').trim() || 'Information';
      }
      return title;
    }
    return 'Information';
  }

  private mapIcon(icon: SweetAlertOptions['icon']): MobileAlertTone {
    if (icon === 'success' || icon === 'error' || icon === 'warning' || icon === 'info' || icon === 'question') {
      return icon;
    }
    return 'info';
  }

  private mapConfirmTone(options: SweetAlertOptions): MobileAlertConfig['confirmTone'] {
    const color = String(options.confirmButtonColor || '').toLowerCase();
    if (color.includes('dc') || color.includes('ef44') || color.includes('d33') || color.includes('e74')) {
      return 'danger';
    }
    if (color.includes('28a745') || color.includes('10b981') || color.includes('27ae')) {
      return 'success';
    }
    if (options.icon === 'error' || options.icon === 'warning') {
      return options.icon === 'error' ? 'danger' : 'primary';
    }
    return 'primary';
  }
}
